import {
  ControlEmptyState,
  filterOptionGroupsOnGroupNameOnly,
  GreyGrey70,
  InputSelectControl,
  Spinner,
  Center,
  Box,
  Link
} from '@sequencehq/core-components'
import { useSearchForProduct } from 'Cube/view/common/widgets/Pricing/Phase/SearchForProduct/useSearchForProduct'
import { Phase } from 'modules/Cube/domain/cube.domain.types'
import { FC } from 'react'
import { IndigoIndigo60 } from '@sequencehq/core-components'

type SearchForProductProps = {
  phaseId: Phase['id']
  onSelection: (productId: string) => void
  onBlur: () => void
}

export const SearchForProduct: FC<SearchForProductProps> = props => {
  const {
    productOptionsWithListPrices,
    onSelection,
    onAddNew,
    showAllProductsAddedStateWhenEmpty,
    isLoading
  } = useSearchForProduct({
    onSelection: props.onSelection,
    phaseId: props.phaseId
  })

  const loadingState = (
    <Center padding={4}>
      <Spinner size="sm" color={GreyGrey70} />
    </Center>
  )

  const emptyState = showAllProductsAddedStateWhenEmpty ? (
    <AllProductsAddedState />
  ) : (
    <EmptyState onClick={onAddNew} />
  )

  return (
    <Box position="relative" width="376px">
      <InputSelectControl
        filterOptionGroups={filterOptionGroupsOnGroupNameOnly}
        initiallyOpen
        matchWidth
        data-testid="phase.searchForProduct"
        options={productOptionsWithListPrices}
        onChange={onSelection}
        placeholder="Find or create a new price"
        onToggle={(isOpen: boolean) => {
          requestAnimationFrame(() => {
            !isOpen && props.onBlur()
          })
        }}
        emptyState={isLoading ? loadingState : emptyState}
        noResultsState={<NoResultsState />}
      />
    </Box>
  )
}

const EmptyState: FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <ControlEmptyState
      title="Create your first product"
      content="You have no products configured. Head over to the product catalogue to create your first product"
      button={{
        text: 'Add new',
        onClick
      }}
    />
  )
}

const AllProductsAddedState = () => {
  return (
    <ControlEmptyState
      title="No products available"
      content="All your active products are currently used in this phase."
    />
  )
}

const NoResultsState = () => {
  return (
    <ControlEmptyState
      title="No results"
      content={
        <Box>
          Head over to the{' '}
          <Link.Unstyled
            to="/products"
            target="_blank"
            style={{ color: IndigoIndigo60 }}
          >
            product catalogue
          </Link.Unstyled>{' '}
          to create and manage your products and list prices.
        </Box>
      }
    />
  )
}
